import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { createId } from '@paralleldrive/cuid2'
import {
  IconCamera,
  IconLibraryPhoto,
  IconPhotoPlus,
} from '@tabler/icons-react'
import { Image, message } from 'antd'
import { RcFile } from 'antd/lib/upload'
import { throttle } from 'lodash'
import styled, { css } from 'styled-components'

import { Flexbox } from '../Flexbox'

import { CameraModal, hasCameraSupport } from './CameraModal'
import { Popover, PopoverItem, PopoverItemNaked } from './Popover'
import { Text } from './Text'
import { UploaderAttachments } from './UploaderAttachments'

import { ACCEPT } from '@/config/constants/fileTypes'
import { usePreventSaving } from '@/context/preventSavingContext'
import {
  AttachmentCompressionOptions,
  FileUploadResult,
  useAttachmentUpload,
} from '@/hooks/useAttachmentUpload'
import { MarkupModal } from '@/modules/defects/MarkupModal'
import { pluralise } from '@/utils/index'

import {
  AttachmentType,
  CreateAttachmentUiMutationVariables,
  Resource,
} from 'types/api.graphql'

const SIZE = 72
const PARALLEL_UPLOADS = 4

const MOBILE_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Safari/i

const LabelWrapper = styled.label``

const UploaderWrapper = styled.div`
  position: relative;
  width: ${SIZE / 16}rem;
  height: ${SIZE / 16}rem;
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background-color: ${({ theme }) => theme.colour.secondary._50};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border: 2px dashed ${({ theme }) => theme.colour.secondary._400};
  }
`

const AddMedia = styled(Text)`
  position: absolute;
  bottom: 0.75rem;
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  font-size: 0.5rem;
  text-align: center;
`

const MaxCount = styled(Text)`
  font-size: 0.5rem;
  position: absolute;
  bottom: 0.125rem;
  right: 0.125rem;
`

const UploaderContainer = styled.div<{
  $isHovered: boolean
  $hasCustomUploader: boolean
}>`
  ${({ $hasCustomUploader, $isHovered }) =>
    $hasCustomUploader
      ? ''
      : css`
          margin-bottom: 0.5rem;
          border-radius: ${({ theme }) => theme.borderRadius.large};
          border: 1px dashed ${({ theme }) => theme.colour.secondary._100};
          padding: 1rem;

          ${$isHovered &&
          css`
            border: 1px dashed transparent;
            background-color: ${({ theme }) => theme.colour.neutral.surface};
            box-shadow: ${({ theme }) =>
              theme.colour.primary._100 + ' 0px 0px 0px 2px'};
          `};
        `}
`

enum State {
  Loading = 'loading',
  Mobile = 'mobile',
  Desktop = 'desktop',
}

type Props = {
  projectId: string // used to link the attachment to the project, 99% of cases you want to supply this
  attachmentType: AttachmentType
  attachmentResource: Resource
  mutationVariables?: Partial<CreateAttachmentUiMutationVariables['input']>
  isPublic?: boolean
  compressionPreset?: AttachmentCompressionOptions['preset']
  createAttachment?: boolean
  onUploadStatusChange?: (status: 'uploading' | 'done' | 'error') => void
  renderingInPdf?: boolean
  attachmentSortDate?: string
  accept?: string
  maxCount?: number
  customUploader?: (
    maxCount: number,
    fileCount: number,
    fileInput: HTMLInputElement,
    addFiles: (files: RcFile[]) => Promise<void>
  ) => React.ReactNode
  renderAttachmentsElementId?: string

  fileList?: FileUploadResult[]
  onChange?: (value: FileUploadResult[]) => void
  enableMarkup?: boolean

  containerStyle?: React.CSSProperties
}

export const Uploader: React.FC<Props> = ({
  fileList = [],
  onChange,
  attachmentResource,
  attachmentType,
  mutationVariables,
  attachmentSortDate,
  projectId,
  isPublic,
  compressionPreset,
  createAttachment = true,
  onUploadStatusChange,
  renderingInPdf,
  accept = ACCEPT,
  maxCount,
  renderAttachmentsElementId,
  customUploader,
  enableMarkup = false,
  containerStyle,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const filesWaitingForUpload = useRef<RcFile[]>([]) // keeping track of files that are in queue to upload
  const memoedFileList = useRef(fileList) // used to keep track of the files that are being uploaded, required when uploading multiple files at once

  const { setIsSavingBlocked } = usePreventSaving()
  const { uploadAttachment, validateFileSize, validateFileType } =
    useAttachmentUpload()

  const [id, setId] = useState<string>()
  const [state, setState] = useState(State.Loading)
  const [modalOpen, setModalOpen] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [markupFile, setMarkupFile] = useState<FileUploadResult>(null)
  const [isDragging, setIsDragging] = useState(false)

  const closeMarkupModal = () => setMarkupFile(null)

  // decide if the user is on mobile or desktop
  useEffect(() => {
    setId(createId()) // done this way because SSR caused issues where the id was different on the client and server

    if (typeof global?.window?.navigator === 'undefined') {
      setState(State.Desktop)
      return
    }

    if (MOBILE_REGEX.test(navigator.userAgent)) {
      hasCameraSupport().then((hasSupport) => {
        setState(hasSupport ? State.Mobile : State.Desktop)
      })
    } else {
      setState(State.Desktop)
    }
  }, [])

  const onProgress = useMemo(() => {
    const throttled = throttle((event: { percent: number }, file: RcFile) => {
      const newFiles = memoedFileList.current.map((f) =>
        f.uid === file.uid ? { ...f, percent: event.percent } : { ...f }
      )
      onChange?.(newFiles)
      memoedFileList.current = newFiles
    }, 100) // Update at most every 100ms

    return throttled
  }, [onChange])

  const onUpload = useCallback(
    (upload: FileUploadResult) => {
      const newFiles = memoedFileList.current.map((file) => {
        return file.uid === upload.uid ? upload : { ...file }
      })

      onChange?.(newFiles)
      memoedFileList.current = newFiles
    },
    [onChange]
  )

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    addFiles(Array.from(fileList) as RcFile[])
  }

  const startUploadingPhotos = useCallback(async () => {
    const nextFiles = filesWaitingForUpload.current.splice(0, PARALLEL_UPLOADS)

    const uploadPromises = nextFiles.map((file) => {
      return uploadAttachment(file, {
        attachmentResource,
        attachmentType,
        isPublic,
        onUpload,
        onProgress,
        createAttachment,
        attachmentSortDate,
        mutationVariables: { ...mutationVariables, projectId },
        compressionOptions: compressionPreset
          ? {
              preset: compressionPreset,
            }
          : undefined,
      })
    })

    return Promise.all(uploadPromises)
      .catch(() => onUploadStatusChange?.('error'))
      .then(() => {
        if (filesWaitingForUpload.current.length) {
          startUploadingPhotos()
        } else {
          onUploadStatusChange?.('done')
          setIsSavingBlocked(false)
        }
      })
  }, [
    attachmentResource,
    attachmentSortDate,
    attachmentType,
    compressionPreset,
    createAttachment,
    isPublic,
    mutationVariables,
    onProgress,
    onUpload,
    onUploadStatusChange,
    projectId,
    setIsSavingBlocked,
    uploadAttachment,
  ])

  const addFiles = useCallback(
    async (files: RcFile[]) => {
      let validFiles = files
        .filter((file) => {
          return validateFileSize()(file) && validateFileType(accept)(file)
        })
        .map((file) => {
          const f = file
          f.uid = createId()
          return f
        })

      if (maxCount !== undefined) {
        if (fileList?.length + validFiles.length > maxCount) {
          validFiles = validFiles.slice(0, maxCount - fileList.length)
          message.warning(`You can only upload ${pluralise(maxCount, 'file')}`)
        }
      }

      if (!validFiles.length) return

      setIsSavingBlocked(true)
      onUploadStatusChange?.('uploading')

      const newFiles = [
        ...(memoedFileList.current ?? []),
        ...validFiles.map<FileUploadResult>((f) => ({
          uid: f.uid,
          name: f.name,
          percent: 0,
        })),
      ]

      onChange?.(newFiles)
      memoedFileList.current = newFiles

      filesWaitingForUpload.current = [
        ...filesWaitingForUpload.current,
        ...validFiles,
      ]

      return startUploadingPhotos()
    },
    [
      accept,
      fileList.length,
      maxCount,
      onChange,
      onUploadStatusChange,
      setIsSavingBlocked,
      startUploadingPhotos,
      validateFileSize,
      validateFileType,
    ]
  )

  const onDropFiles = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    e.dataTransfer.files &&
      addFiles(Array.from(e.dataTransfer.files) as RcFile[])
  }

  const removeFile = (uid: string) => {
    filesWaitingForUpload.current = filesWaitingForUpload.current.filter(
      (f) => f.uid !== uid
    )

    const newFiles = memoedFileList.current?.filter(
      (f) => f.uid !== uid && f.attachmentId !== uid // when an attachment on a block, the uid is the attachmentId
    )

    onChange?.(newFiles)
    memoedFileList.current = newFiles
  }

  const uploader = useMemo(() => {
    if (customUploader) {
      return customUploader(
        maxCount ?? 0,
        fileList?.length ?? 0,
        state === State.Desktop ? inputRef.current : undefined,
        addFiles
      )
    }

    return (
      <UploaderWrapper>
        {maxCount !== undefined && (
          <MaxCount variant="caption02" secondary="_400">
            {fileList?.length}/{maxCount}
          </MaxCount>
        )}
        <IconPhotoPlus size={18} />
        <AddMedia variant="caption01" mt="0.25rem">
          Add media
        </AddMedia>
      </UploaderWrapper>
    )
  }, [addFiles, customUploader, fileList?.length, maxCount, state])

  const popoverAction = (callback?: () => void) => () => {
    setPopoverOpen(false)
    callback?.()
  }

  return (
    <UploaderContainer
      onDrop={onDropFiles}
      onDragOver={(e) => {
        e.preventDefault()
        setIsDragging(true)
      }}
      $hasCustomUploader={!!customUploader}
      onDragLeave={() => setIsDragging(false)}
      $isHovered={isDragging}
    >
      <Image.PreviewGroup>
        <Flexbox gap="0.5rem 0.75rem" flexWrap="wrap" style={containerStyle}>
          {state === State.Mobile && (
            <Popover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              content={
                <>
                  <PopoverItemNaked>
                    <Text variant="subheading02">Add media</Text>
                  </PopoverItemNaked>
                  <PopoverItem
                    as="label"
                    htmlFor={id}
                    onClick={popoverAction()}
                  >
                    <IconLibraryPhoto size={16} /> Choose photos / files
                  </PopoverItem>
                  <PopoverItem
                    onClick={popoverAction(() => setModalOpen(true))}
                  >
                    <IconCamera size={16} /> Take photos
                  </PopoverItem>
                </>
              }
              placement="right"
            >
              {uploader}
            </Popover>
          )}

          {state === State.Desktop && (
            <LabelWrapper htmlFor={id}>{uploader}</LabelWrapper>
          )}

          <input
            type="file"
            id={id}
            hidden
            ref={inputRef}
            onChange={onInputChange}
            value=""
            multiple={!maxCount || maxCount > 1}
            accept={accept}
          />

          <CameraModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            addFiles={addFiles}
            onLibraryPhotoClick={() => inputRef.current?.click()}
          />

          <UploaderAttachments
            size={SIZE}
            fileList={fileList}
            removeFile={removeFile}
            renderingInPdf={renderingInPdf}
            renderAttachmentsElementId={renderAttachmentsElementId}
            onEdit={enableMarkup ? (file) => setMarkupFile(file) : undefined}
          />
        </Flexbox>
      </Image.PreviewGroup>
      {markupFile && (
        <MarkupModal
          file={markupFile}
          closeModal={closeMarkupModal}
          insertFile={(newFile) => addFiles([newFile])}
        />
      )}
    </UploaderContainer>
  )
}
